import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateProjectsSearchTerm } from "../reducers";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import MobileEditProjectDialog from "./dialogs/MobileEditProjectDialog";
import ThemedTypography from "./ThemedTypography";
import ProjectSearchBar from "./ProjectSearchBar";
import { decoratedFetch } from "../req_utils";
import {
  GENESIS_LOGO_COLOR,
  DARK_THEME_BODY_COLOR,
  LIGHT_THEME_BODY_COLOR,
  DARK_THEME_TEXT_COLOR,
  DARK_THEME_DISABLED_TEXT_COLOR,
  DARK_THEME_INPUT_OUTLINE_COLOR,
} from "../constants";
import MobileCreateProjectDialog from "./dialogs/MobileCreateProjectDialog";

const MobileProjectList = () => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const employee_branch_id = useSelector((state) => state.employee_branch_id);
  const searchTerm = useSelector((state) => state.projects_search_term);
  const dispatch = useDispatch();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [projectDialogState, setProjectDialogState] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const listProjects = (projectId = null) => {
    if (!employee_branch_id) return;
    
    decoratedFetch(`/list_projects?mobile=true&branch_id=${employee_branch_id}`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setProjects(data);
        setFilteredProjects(data);
        if (projectId) {
          setProjectDialogState({
            selectedProject: data.filter((proj) => proj.id === projectId)[0],
            showCreateProjectView: false,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        setSnackbarMessage("Failed to load projects");
        setLoading(false);
        console.error(`Error listing projects: ${error}`);
      });
  };

  // Handle search changes from ProjectSearchBar (persisted in Redux)
  const handleSearchChange = useCallback((value) => {
    dispatch(updateProjectsSearchTerm(value ?? ""));
  }, [dispatch]);

  // Filter projects when search term or projects list changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProjects(projects);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = projects.filter((project) => (
        project.name?.toLowerCase().includes(searchLower) ||
        project.custom_id?.toLowerCase().includes(searchLower) ||
        project.account_name?.toLowerCase().includes(searchLower) ||
        project.assignee_name?.toLowerCase().includes(searchLower) ||
        project.status_name?.toLowerCase().includes(searchLower) ||
        project.consultant_name?.toLowerCase().includes(searchLower) ||
        project.cmas_type?.toLowerCase().includes(searchLower)
      ));
      setFilteredProjects(filtered);
    }
  }, [projects, searchTerm]);

  useEffect(() => {
    if (!employee_branch_id) return;
    setLoading(true);
    listProjects();
  }, [employee_branch_id]);

  const refreshProjectsAndSelectProject = (projectId) => {
    listProjects(projectId);
  };

  const handleCreateProject = (projectId) => {
    setCreateDialogOpen(false);
    listProjects(projectId);
  };

  const handleProjectUpdate = (updatedProject) => {
    setProjects((prevProjects) =>
      prevProjects.map((proj) =>
        proj.id === updatedProject.id
          ? {
              ...proj,
              name: updatedProject.name,
              assignee_name: updatedProject.assignee
                ? `${updatedProject.assignee.first_name} ${updatedProject.assignee.last_name}`
                : "None",
              status_name: updatedProject.status.name,
            }
          : proj,
      ),
    );
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <ProjectSearchBar
        value={searchTerm}
        onSearchChange={handleSearchChange}
        placeholder="Search projects by name, account, project id, consultant, or cmas type..."
        isMobile={true}
      />

      <Snackbar
        sx={{
          position: "fixed",
          left: 8,
          right: 8,
          bottom: 64, // 8px closer to bottom nav, with margin on sides
          zIndex: (theme) => theme.zIndex.snackbar,
          ".MuiSnackbarContent-root": {
            backgroundColor: GENESIS_LOGO_COLOR,
            minWidth: 0,
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
      <MobileEditProjectDialog
        parentState={projectDialogState}
        refreshProjectsAndSelectProject={refreshProjectsAndSelectProject}
        handleClose={() => setProjectDialogState(null)}
        onProjectUpdate={handleProjectUpdate}
      />
      <MobileCreateProjectDialog
        open={createDialogOpen}
        handleClose={() => setCreateDialogOpen(false)}
        onCreateProject={handleCreateProject}
      />
      {filteredProjects?.length ? (
        <List sx={{ py: 0, flex: 1, pb: 2 }}>
          {filteredProjects?.map((project, index) => (
            <>
              <ListItemButton
                key={index}
                onClick={() =>
                  setProjectDialogState({
                    selectedProject: filteredProjects[index],
                    showCreateProjectView: false,
                  })
                }
              >
                <ListItemText
                  primary={`${project.name} (${project.custom_id})`}
                  secondary={
                    <>
                      <p style={{ marginTop: "4px", marginBottom: 0 }}>
                        <strong>{"Account: "}</strong>
                        {project?.account_name}
                      </p>
                      <p style={{ marginTop: "4px", marginBottom: 0 }}>
                        <strong>{"Assignee: "}</strong>
                        {project?.assignee_name || "None"}
                      </p>
                      <p style={{ marginTop: "4px", marginBottom: 0 }}>
                        <strong>{"Status: "}</strong>
                        {project?.status_name}
                      </p>
                    </>
                  }
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: "bold",
                      color: darkThemeEnabled
                        ? DARK_THEME_TEXT_COLOR
                        : "inherit",
                    },
                  }}
                  secondaryTypographyProps={{
                    sx: {
                      color: darkThemeEnabled
                        ? DARK_THEME_DISABLED_TEXT_COLOR
                        : "inherit",
                    },
                  }}
                />
              </ListItemButton>
              {index < filteredProjects.length - 1 && (
                <Divider
                  component="li"
                  sx={{
                    backgroundColor: darkThemeEnabled
                      ? DARK_THEME_INPUT_OUTLINE_COLOR
                      : "inherited",
                  }}
                />
              )}
            </>
          ))}
        </List>
      ) : loading ? (
        <Box
          sx={{
            alignSelf: "center",
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size="50px" />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ThemedTypography>
            {filteredProjects.length === 0 && projects.length > 0 ? "No projects found" : "No projects"}
          </ThemedTypography>
        </Box>
      )}
      <Fab
        color="primary"
        aria-label="add"
        size="medium"
        sx={{ position: "fixed", bottom: 64, right: 16 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default MobileProjectList;
